import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { C } from "../../lib/colors";
import { useT } from "../LangContext";
import GuideBody from "./GuideBody";
import { subscribeGuideDrawer } from "../../lib/guideDrawer";

// ─── Fase 17B · Bloque 2 — Right-side drawer hosting the full guide ────
// • Opens on `pf:guide-drawer-open` window event (dispatched by
//   `openGuideDrawer()` in /app/frontend/src/lib/guideDrawer.js).
// • Closes on ESC, ✕ button, or overlay click.
// • Locks body scroll while open.
// • The inner scroll container has id `pf-guide-drawer-scroll` so the
//   TOC's smooth-scroll resolves to it (see GuideBody.smoothScroll).
// • role="dialog" + aria-label for a11y.

export default function GuideDrawer() {
  const { t } = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const scrollRef = useRef(null);

  // Subscribe to imperative open events
  useEffect(() => subscribeGuideDrawer(() => setOpen(true)), []);

  // ESC handler + body scroll lock
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Reset internal scroll on each open
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div data-testid="guide-drawer-root"
         role="dialog"
         aria-modal="true"
         aria-label={t("guide_drawer_aria")}
         style={{ position: "fixed", inset: 0, zIndex: 90,
                   display: "flex", justifyContent: "flex-end" }}>
      {/* Overlay */}
      <div data-testid="guide-drawer-overlay"
           onClick={() => setOpen(false)}
           style={{ position: "absolute", inset: 0,
                     background: "rgba(11, 15, 16, 0.78)",
                     backdropFilter: "blur(2px)",
                     WebkitBackdropFilter: "blur(2px)" }} />

      {/* Drawer */}
      <aside data-testid="guide-drawer"
             style={{ position: "relative", height: "100%",
                       width: "min(420px, 100vw)",
                       background: C.ink, borderLeft: `1px solid ${C.dust}`,
                       display: "flex", flexDirection: "column",
                       boxShadow: "-12px 0 32px rgba(0,0,0,0.45)",
                       animation: "pf-drawer-in 220ms ease-out" }}>
        {/* Header */}
        <header style={{ padding: "14px 18px",
                          borderBottom: `1px solid ${C.dust}`,
                          display: "flex", alignItems: "center",
                          justifyContent: "space-between", gap: 10,
                          background: "rgba(42, 48, 50, 0.20)" }}>
          <div>
            <div style={{ color: C.brass, fontFamily: "var(--mono)", fontSize: 10,
                            letterSpacing: 0.22, textTransform: "uppercase" }}>
              {t("guide_drawer_kicker")}
            </div>
            <div style={{ color: C.bone, fontFamily: "var(--plex)", fontSize: 13,
                            marginTop: 2 }}>
              {t("guide_drawer_title")}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button data-testid="guide-drawer-open-page"
                    onClick={() => { setOpen(false); navigate("/guide"); }}
                    style={{ background: "transparent", border: `1px solid ${C.dust}`,
                              color: C.smoke, padding: "5px 10px", cursor: "pointer",
                              fontFamily: "var(--mono)", fontSize: 10.5,
                              letterSpacing: 0.18 }}>
              {t("guide_open_page")}
            </button>
            <button data-testid="guide-drawer-close"
                    onClick={() => setOpen(false)}
                    aria-label={t("guide_drawer_close")}
                    style={{ background: "transparent", border: "none",
                              color: C.linen, padding: 6, cursor: "pointer",
                              fontSize: 18, lineHeight: 1 }}>
              ✕
            </button>
          </div>
        </header>

        {/* Scrollable body */}
        <div id="pf-guide-drawer-scroll" ref={scrollRef}
             style={{ flex: 1, overflowY: "auto", padding: "20px 22px",
                       background: C.ink }}>
          <GuideBody containerSelector="#pf-guide-drawer-scroll" />
        </div>
      </aside>

      <style>{`
        @keyframes pf-drawer-in {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
